import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserCircle, Lock, CreditCard, Store, ShoppingBag, Wallet, ArrowDownToLine, Users as TeamIcon, ShieldCheck, Trash2, Copy, Eye, EyeOff } from "lucide-react";

const TABS = [
  { key: "account", label: "Akun", icon: UserCircle },
  { key: "password", label: "Kata Sandi", icon: Lock },
  { key: "banks", label: "Rekening Bank", icon: CreditCard },
  { key: "seller", label: "Mode Penjual", icon: Store },
];

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const [tab, setTab] = useState("account");

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="profile-page">
      <div className="label-mono text-zinc-500">// MY ACCOUNT</div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-1">Profil Saya</h1>

      <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-3">
          <div className="border border-black bg-[#FF6A00] text-white p-5 shadow-brutal">
            <div className="font-display text-2xl font-black uppercase tracking-tight break-all">{user.name}</div>
            <div className="font-mono text-xs mt-1 opacity-90">{user.phone}</div>
            <div className="mt-4 pt-3 border-t border-black/30">
              <div className="label-mono text-white/80">// SALDO</div>
              <div className="font-mono text-2xl font-black mt-1">${Number(user.balance).toFixed(2)}</div>
            </div>
            <div className="mt-3 flex gap-2">
              <Link to="/topup" className="flex-1 text-center bg-black text-white py-2 font-mono text-[10px] font-bold uppercase border border-black hover:bg-white hover:text-black">+ Isi</Link>
              <Link to="/withdraw" className="flex-1 text-center bg-white text-black py-2 font-mono text-[10px] font-bold uppercase border border-black hover:bg-black hover:text-white">Tarik</Link>
            </div>
          </div>

          <nav className="border border-black bg-white shadow-brutal">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 border-b border-zinc-200 last:border-b-0 font-mono text-xs uppercase font-bold tracking-widest ${tab === t.key ? "bg-black text-white" : "hover:bg-zinc-50"}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>

          <div className="border border-black bg-white">
            <div className="p-4 border-b border-black label-mono">// SHORTCUTS</div>
            <div className="p-2 space-y-1 text-sm">
              <Link to="/orders" className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 font-mono text-xs uppercase font-bold"><ShoppingBag className="w-3.5 h-3.5" /> Pesanan</Link>
              <Link to="/team" className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 font-mono text-xs uppercase font-bold"><TeamIcon className="w-3.5 h-3.5" /> Tim & Referral</Link>
              <Link to="/topup" className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 font-mono text-xs uppercase font-bold"><Wallet className="w-3.5 h-3.5" /> Isi Saldo</Link>
              <Link to="/withdraw" className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 font-mono text-xs uppercase font-bold"><ArrowDownToLine className="w-3.5 h-3.5" /> Tarik Dana</Link>
              {user.role === "admin" && (
                <Link to="/admin" className="flex items-center gap-2 px-3 py-2 bg-[#FFD600] font-mono text-xs uppercase font-bold border border-black mt-2"><ShieldCheck className="w-3.5 h-3.5" /> Admin Panel</Link>
              )}
            </div>
          </div>
        </aside>

        <section className="border border-black bg-white p-6 shadow-brutal min-h-[400px]">
          {tab === "account" && <AccountTab user={user} refresh={refresh} />}
          {tab === "password" && <PasswordTab logout={logout} />}
          {tab === "banks" && <BanksTab />}
          {tab === "seller" && <SellerTab />}
        </section>
      </div>
    </div>
  );
}

function AccountTab({ user, refresh }) {
  const [name, setName] = useState(user.name);
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/auth/me", { name });
      toast.success("Profil diperbarui");
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(user.referral_code);
    toast.success("Kode referral disalin");
  };

  return (
    <div data-testid="tab-content-account">
      <div className="label-mono text-zinc-500">// DETAIL AKUN</div>
      <h2 className="font-display text-2xl font-black uppercase mt-1 mb-4">Informasi Pribadi</h2>

      <form onSubmit={save} className="space-y-4 max-w-md">
        <div>
          <label className="label-mono block mb-2">Nama</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="input-brutal" required data-testid="account-name" />
        </div>
        <div>
          <label className="label-mono block mb-2">No. Handphone</label>
          <input value={user.phone} disabled className="input-brutal bg-zinc-100" />
          <p className="text-[11px] font-mono text-zinc-500 mt-1">No. HP tidak bisa diubah.</p>
        </div>
        <div>
          <label className="label-mono block mb-2">Saldo</label>
          <div className="input-brutal bg-zinc-100 font-bold">${Number(user.balance).toFixed(2)}</div>
        </div>
        <div>
          <label className="label-mono block mb-2">Kode Referral Kamu</label>
          <div className="flex gap-2">
            <input value={user.referral_code} disabled className="input-brutal bg-[#FFD600] font-bold tracking-widest" />
            <button type="button" onClick={copyRef} className="p-3 border border-black hover:bg-black hover:text-white" data-testid="copy-ref"><Copy className="w-4 h-4" /></button>
          </div>
        </div>
        <button disabled={loading} className="btn-primary" data-testid="save-account">{loading ? "// SAVING..." : "Simpan"}</button>
      </form>
    </div>
  );
}

function PasswordTab({ logout }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ current: false, new: false, confirm: false });

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password berhasil diubah. Silakan login ulang.");
      setTimeout(() => logout(), 1500);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="tab-content-password">
      <div className="label-mono text-zinc-500">// SECURITY</div>
      <h2 className="font-display text-2xl font-black uppercase mt-1 mb-4">Ubah Kata Sandi</h2>
      <form onSubmit={save} className="space-y-4 max-w-md">
        <div>
          <label className="label-mono block mb-2">Password saat ini</label>
          <div className="relative">
            <input type={show.current ? "text" : "password"} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="input-brutal w-full pr-12" required data-testid="pwd-current" />
            <button type="button" onClick={() => setShow({ ...show, current: !show.current })} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black">
              {show.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="label-mono block mb-2">Password baru</label>
          <div className="relative">
            <input type={show.new ? "text" : "password"} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="input-brutal w-full pr-12" required minLength={6} data-testid="pwd-new" />
            <button type="button" onClick={() => setShow({ ...show, new: !show.new })} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black">
              {show.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="label-mono block mb-2">Konfirmasi password baru</label>
          <div className="relative">
            <input type={show.confirm ? "text" : "password"} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="input-brutal w-full pr-12" required minLength={6} data-testid="pwd-confirm" />
            <button type="button" onClick={() => setShow({ ...show, confirm: !show.confirm })} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black">
              {show.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <button disabled={loading} className="btn-primary" data-testid="save-password">{loading ? "// SAVING..." : "Ubah Password"}</button>
      </form>
    </div>
  );
}

function BanksTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ bank_name: "", account_no: "", account_name: "", label: "" });
  const load = () => api.get("/me/bank-accounts").then((r) => setItems(r.data));
  useEffect(load, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/bank-accounts", form);
      toast.success("Rekening ditambahkan");
      setForm({ bank_name: "", account_no: "", account_name: "", label: "" });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus rekening?")) return;
    await api.delete(`/me/bank-accounts/${id}`);
    load();
  };
  return (
    <div data-testid="tab-content-banks">
      <div className="label-mono text-zinc-500">// PAYMENT METHODS</div>
      <h2 className="font-display text-2xl font-black uppercase mt-1 mb-4">Rekening Bank / E-Wallet</h2>

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 mb-6">
        <input placeholder="Bank / E-wallet" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="input-brutal" required data-testid="bank-name" />
        <input placeholder="Label (mis: Utama)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-brutal" />
        <input placeholder="No. Rekening" value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} className="input-brutal" required data-testid="bank-acc-no" />
        <input placeholder="Nama Pemilik" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="input-brutal" required data-testid="bank-acc-name" />
        <button className="btn-primary sm:col-span-2" data-testid="bank-submit">+ Tambah Rekening</button>
      </form>

      {items.length === 0 ? (
        <div className="p-8 text-center font-mono text-xs text-zinc-500 border border-dashed border-zinc-300">// Belum ada rekening tersimpan</div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => (
            <div key={b.id} className="border border-black p-4 flex justify-between items-center" data-testid={`bank-${b.id}`}>
              <div>
                <div className="font-mono text-sm font-bold">{b.bank_name} · {b.account_no}</div>
                <div className="font-mono text-xs text-zinc-600">{b.account_name} {b.label && <span className="badge-status bg-[#FFD600] ml-2">{b.label}</span>}</div>
              </div>
              <button onClick={() => remove(b.id)} className="p-2 border border-black hover:bg-[#FF3333] hover:text-white" data-testid={`bank-del-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SellerTab() {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ shop_name: "", shop_description: "" });

  const load = () => {
    setLoading(true);
    api.get("/seller/me").then((r) => { setSeller(r.data); setLoading(false); });
  };
  useEffect(load, []);

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seller/apply", form);
      toast.success("Selamat! Akun penjual aktif.");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading) return <div className="font-mono">// LOADING</div>;

  if (!seller) {
    return (
      <div data-testid="tab-content-seller-apply">
        <div className="label-mono text-zinc-500">// BECOME SELLER</div>
        <h2 className="font-display text-2xl font-black uppercase mt-1 mb-4">Jadi Penjual</h2>
        <p className="text-sm text-zinc-600 mb-4 max-w-lg">
          Mulai jualan produk digital atau fisik di AleShopNow. Biaya iklan 0%, audience referral built-in. Pendaftaran gratis & langsung aktif.
        </p>
        <form onSubmit={apply} className="space-y-4 max-w-md">
          <div>
            <label className="label-mono block mb-2">Nama Toko</label>
            <input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className="input-brutal" required data-testid="shop-name" />
          </div>
          <div>
            <label className="label-mono block mb-2">Deskripsi Toko</label>
            <textarea rows={3} value={form.shop_description} onChange={(e) => setForm({ ...form, shop_description: e.target.value })} className="input-brutal" placeholder="Singkat saja, apa yang kamu jual" data-testid="shop-desc" />
          </div>
          <button className="btn-primary" data-testid="shop-apply">Daftar Penjual →</button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="tab-content-seller-dashboard">
      <div className="label-mono text-zinc-500">// SELLER DASHBOARD</div>
      <h2 className="font-display text-2xl font-black uppercase mt-1 mb-4 break-all">{seller.shop_name}</h2>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-black bg-white p-4">
          <div className="label-mono text-zinc-500">// PRODUK</div>
          <div className="font-display text-2xl font-black mt-1" data-testid="seller-stat-products">{seller.total_products}</div>
        </div>
        <div className="border border-black bg-white p-4">
          <div className="label-mono text-zinc-500">// TERJUAL</div>
          <div className="font-display text-2xl font-black mt-1">{seller.total_sold}</div>
        </div>
        <div className="border border-black bg-black text-white p-4">
          <div className="label-mono text-[#FFD600]">// OMSET</div>
          <div className="font-mono text-xl font-bold mt-1">{formatRupiah(seller.total_revenue)}</div>
        </div>
      </div>

      <Link to="/seller/products" className="btn-primary inline-block" data-testid="seller-open">Kelola Produk →</Link>
    </div>
  );
}